import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BlockTitle,
  List,
  ListItem,
  Toggle,
  Actions,
  ActionsGroup,
  ActionsLabel,
  ActionsButton,
  Button,
} from 'konsta/react'
import {
  User,
  Shield,
  BadgeCheck,
  CreditCard,
  Volume2,
  Smartphone,
  LayoutDashboard,
  Home,
  FileBarChart,
  Globe,
  HelpCircle,
  Info,
  Headphones,
  LogOut,
  TrendingUp,
  Trophy,
  Gamepad2,
  Pencil,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { formatCurrency, formatNumber } from '@/lib/utils'

export function ProfileScreen() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)

  // Settings state
  const [autoBuyIn, setAutoBuyIn] = useState(true)
  const [soundEffects, setSoundEffects] = useState(true)
  const [hapticFeedback, setHapticFeedback] = useState(true)

  // Language sheet
  const [langSheetOpen, setLangSheetOpen] = useState(false)
  const [currentLang, setCurrentLang] = useState('繁體中文')

  // Logout confirmation
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false)

  const handleLogout = () => {
    setLogoutConfirmOpen(false)
    logout()
    navigate('/login')
  }

  const handleSelectLanguage = (lang: string) => {
    setCurrentLang(lang)
    setLangSheetOpen(false)
  }

  // Mock stats
  const stats = {
    gamesPlayed: 128,
    winRate: 62,
    totalProfit: 245000, // $2,450.00 in cents
  }

  return (
    <div className="bg-[#F2F2F7] min-h-full pb-4">
      {/* Profile Hero Card — clean centered layout */}
      <div className="mx-4 mt-3 bg-white rounded-2xl shadow-sm px-4 pt-5 pb-4">
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div className="w-[68px] h-[68px] rounded-full bg-gradient-to-br from-[#007AFF] to-[#5856D6] flex items-center justify-center flex-shrink-0 shadow-md shadow-[#007AFF]/25">
            <span className="text-white text-[28px] font-bold">
              {user?.name?.charAt(0) || '王'}
            </span>
          </div>

          {/* Name + handle */}
          <div className="flex-1 min-w-0">
            <h2 className="text-[20px] font-bold text-black truncate leading-tight">
              {user?.name || '王大明'}
            </h2>
            <p className="text-[14px] text-[#8E8E93] mt-0.5">
              {user?.telegramId || '@daming_wang'}
            </p>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-[12px] text-[#8E8E93]">{user?.country || 'TW'}</span>
              <span className="text-[12px]">{'\u{1F1F9}\u{1F1FC}'}</span>
            </div>
          </div>

          {/* Edit button */}
          <button
            onClick={() => navigate('/profile/edit')}
            className="w-9 h-9 rounded-full bg-[#F2F2F7] flex items-center justify-center flex-shrink-0 active:bg-[#E5E5EA] transition-colors"
          >
            <Pencil className="w-4 h-4 text-[#8E8E93]" />
          </button>
        </div>

        {/* Balance row */}
        <div className="mt-4 flex items-center justify-between bg-[#F2F2F7] rounded-xl px-4 py-3">
          <span className="text-[14px] text-[#8E8E93] font-medium">帳戶餘額</span>
          <span className="text-[20px] font-bold text-[#007AFF]">
            {formatCurrency(user?.balance || 125000)}
          </span>
        </div>
      </div>

      {/* Stats Row */}
      <div className="mx-4 mt-3 grid grid-cols-3 gap-2">
        <div className="bg-white rounded-xl p-3 text-center shadow-sm">
          <div className="w-9 h-9 rounded-full bg-[#007AFF]/10 flex items-center justify-center mx-auto mb-1.5">
            <Gamepad2 className="w-[18px] h-[18px] text-[#007AFF]" />
          </div>
          <p className="text-[17px] font-bold text-black">{formatNumber(stats.gamesPlayed)}</p>
          <p className="text-[11px] text-[#8E8E93] mt-0.5">場次</p>
        </div>
        <div className="bg-white rounded-xl p-3 text-center shadow-sm">
          <div className="w-9 h-9 rounded-full bg-[#34C759]/10 flex items-center justify-center mx-auto mb-1.5">
            <Trophy className="w-[18px] h-[18px] text-[#34C759]" />
          </div>
          <p className="text-[17px] font-bold text-black">{stats.winRate}%</p>
          <p className="text-[11px] text-[#8E8E93] mt-0.5">勝率</p>
        </div>
        <div className="bg-white rounded-xl p-3 text-center shadow-sm">
          <div className="w-9 h-9 rounded-full bg-[#FF9500]/10 flex items-center justify-center mx-auto mb-1.5">
            <TrendingUp className="w-[18px] h-[18px] text-[#FF9500]" />
          </div>
          <p className="text-[17px] font-bold text-black">{formatCurrency(stats.totalProfit)}</p>
          <p className="text-[11px] text-[#8E8E93] mt-0.5">累計盈利</p>
        </div>
      </div>

      {/* Quick Stats Link */}
      <div className="px-4 mt-2">
        <button
          onClick={() => navigate('/stats')}
          className="w-full text-center text-[13px] text-[#007AFF] font-medium py-1.5"
        >
          查看完整統計 →
        </button>
      </div>

      {/* Account Settings */}
      <BlockTitle className="!mt-4 !mb-2">帳戶設定</BlockTitle>
      <List strongIos insetIos className="!mt-0">
        <ListItem
          link
          chevronIos
          title="個人資料"
          media={
            <div className="w-7 h-7 rounded-md bg-[#007AFF] flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
          }
          onClick={() => navigate('/profile/edit')}
        />
        <ListItem
          link
          chevronIos
          title="安全設定"
          media={
            <div className="w-7 h-7 rounded-md bg-[#FF9500] flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
          }
          onClick={() => navigate('/profile/security')}
        />
        <ListItem
          link
          chevronIos
          title="驗證狀態"
          media={
            <div className="w-7 h-7 rounded-md bg-[#34C759] flex items-center justify-center">
              <BadgeCheck className="w-4 h-4 text-white" />
            </div>
          }
          after={
            <span className="text-[11px] font-semibold text-[#34C759] bg-[#34C759]/10 px-2 py-0.5 rounded-full">
              已驗證
            </span>
          }
          onClick={() => navigate('/profile/verification')}
        />
      </List>

      {/* Game Settings */}
      <BlockTitle className="!mb-2">遊戲設定</BlockTitle>
      <List strongIos insetIos className="!mt-0">
        <ListItem
          label
          title="自動買入"
          media={
            <div className="w-7 h-7 rounded-md bg-[#5856D6] flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-white" />
            </div>
          }
          after={
            <Toggle
              component="div"
              className="-my-1"
              checked={autoBuyIn}
              onChange={() => setAutoBuyIn(!autoBuyIn)}
            />
          }
        />
        <ListItem
          link
          chevronIos
          title="預設買入金額"
          media={
            <div className="w-7 h-7 rounded-md bg-[#5856D6]/80 flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-white" />
            </div>
          }
          after={<span className="text-[#8E8E93] text-[13px]">$100.00</span>}
        />
        <ListItem
          label
          title="聲音效果"
          media={
            <div className="w-7 h-7 rounded-md bg-[#FF2D55] flex items-center justify-center">
              <Volume2 className="w-4 h-4 text-white" />
            </div>
          }
          after={
            <Toggle
              component="div"
              className="-my-1"
              checked={soundEffects}
              onChange={() => setSoundEffects(!soundEffects)}
            />
          }
        />
        <ListItem
          label
          title="震動回饋"
          media={
            <div className="w-7 h-7 rounded-md bg-[#FF2D55]/80 flex items-center justify-center">
              <Smartphone className="w-4 h-4 text-white" />
            </div>
          }
          after={
            <Toggle
              component="div"
              className="-my-1"
              checked={hapticFeedback}
              onChange={() => setHapticFeedback(!hapticFeedback)}
            />
          }
        />
      </List>

      {/* Agent Center */}
      <BlockTitle className="!mb-2">代理中心</BlockTitle>
      <List strongIos insetIos className="!mt-0">
        <ListItem
          link
          chevronIos
          title="代理儀表板"
          media={
            <div className="w-7 h-7 rounded-md bg-[#AF52DE] flex items-center justify-center">
              <LayoutDashboard className="w-4 h-4 text-white" />
            </div>
          }
          onClick={() => navigate('/agent')}
        />
        <ListItem
          link
          chevronIos
          title="我的房間"
          media={
            <div className="w-7 h-7 rounded-md bg-[#AF52DE]/80 flex items-center justify-center">
              <Home className="w-4 h-4 text-white" />
            </div>
          }
          onClick={() => navigate('/rooms')}
        />
        <ListItem
          link
          chevronIos
          title="佣金報告"
          media={
            <div className="w-7 h-7 rounded-md bg-[#AF52DE]/60 flex items-center justify-center">
              <FileBarChart className="w-4 h-4 text-white" />
            </div>
          }
          onClick={() => navigate('/agent/commission')}
        />
      </List>

      {/* Other */}
      <BlockTitle className="!mb-2">其他</BlockTitle>
      <List strongIos insetIos className="!mt-0">
        <ListItem
          link
          chevronIos
          title="語言"
          media={
            <div className="w-7 h-7 rounded-md bg-[#007AFF] flex items-center justify-center">
              <Globe className="w-4 h-4 text-white" />
            </div>
          }
          after={<span className="text-[#8E8E93] text-[13px]">{currentLang}</span>}
          onClick={() => setLangSheetOpen(true)}
        />
        <ListItem
          link
          chevronIos
          title="幫助中心"
          media={
            <div className="w-7 h-7 rounded-md bg-[#8E8E93] flex items-center justify-center">
              <HelpCircle className="w-4 h-4 text-white" />
            </div>
          }
          onClick={() => navigate('/help')}
        />
        <ListItem
          link
          chevronIos
          title="關於 PWG Lounge"
          media={
            <div className="w-7 h-7 rounded-md bg-[#8E8E93]/80 flex items-center justify-center">
              <Info className="w-4 h-4 text-white" />
            </div>
          }
          after={<span className="text-[#8E8E93] text-[13px]">v1.0.0</span>}
        />
        <ListItem
          link
          chevronIos
          title="聯繫客服"
          media={
            <div className="w-7 h-7 rounded-md bg-[#8E8E93]/60 flex items-center justify-center">
              <Headphones className="w-4 h-4 text-white" />
            </div>
          }
          onClick={() => navigate('/support')}
        />
      </List>

      {/* Logout Button */}
      <div className="mx-4 mt-6 mb-8">
        <Button
          large
          className="k-color-brand-red"
          onClick={() => setLogoutConfirmOpen(true)}
        >
          <span className="flex items-center justify-center gap-2">
            <LogOut className="w-4 h-4" />
            登出
          </span>
        </Button>
      </div>

      {/* Language Selection Action Sheet */}
      <Actions
        opened={langSheetOpen}
        onBackdropClick={() => setLangSheetOpen(false)}
      >
        <ActionsGroup>
          <ActionsLabel>選擇語言</ActionsLabel>
          <ActionsButton
            onClick={() => handleSelectLanguage('繁體中文')}
            bold={currentLang === '繁體中文'}
          >
            <span className="flex items-center justify-center gap-2">
              繁體中文
              {currentLang === '繁體中文' && (
                <span className="text-[#007AFF] text-sm">&#10003;</span>
              )}
            </span>
          </ActionsButton>
          <ActionsButton
            onClick={() => handleSelectLanguage('简体中文')}
            bold={currentLang === '简体中文'}
          >
            <span className="flex items-center justify-center gap-2">
              简体中文
              {currentLang === '简体中文' && (
                <span className="text-[#007AFF] text-sm">&#10003;</span>
              )}
            </span>
          </ActionsButton>
          <ActionsButton
            onClick={() => handleSelectLanguage('English')}
            bold={currentLang === 'English'}
          >
            <span className="flex items-center justify-center gap-2">
              English
              {currentLang === 'English' && (
                <span className="text-[#007AFF] text-sm">&#10003;</span>
              )}
            </span>
          </ActionsButton>
        </ActionsGroup>
        <ActionsGroup>
          <ActionsButton onClick={() => setLangSheetOpen(false)}>
            取消
          </ActionsButton>
        </ActionsGroup>
      </Actions>

      {/* Logout Confirmation Action Sheet */}
      <Actions
        opened={logoutConfirmOpen}
        onBackdropClick={() => setLogoutConfirmOpen(false)}
      >
        <ActionsGroup>
          <ActionsLabel>確定要登出嗎？</ActionsLabel>
          <ActionsButton
            onClick={handleLogout}
            bold
            className="k-color-brand-red"
          >
            <span className="text-[#FF3B30] font-semibold">確認登出</span>
          </ActionsButton>
        </ActionsGroup>
        <ActionsGroup>
          <ActionsButton onClick={() => setLogoutConfirmOpen(false)}>
            取消
          </ActionsButton>
        </ActionsGroup>
      </Actions>
    </div>
  )
}
